import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  Panel,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import axios from "../../utils/axios";
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiSettings,
  FiZap,
  FiMessageSquare,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiSave,
  FiPlay,
  FiSearch,
  FiMoreVertical,
  FiCopy,
  FiX,
  FiCheck,
  FiArrowLeft,
  FiRefreshCw,
  FiToggleLeft,
  FiToggleRight,
} from "react-icons/fi";

// ─── Color Palette ────────────────────────────────────────────────────────────
const NODE_CONFIG = {
  trigger: {
    label: "Trigger",
    color: "#16a34a",
    light: "#f0fdf4",
    border: "#86efac",
    emoji: "⚡",
  },
  message: {
    label: "Send Message",
    color: "#2563eb",
    light: "#eff6ff",
    border: "#93c5fd",
    emoji: "💬",
  },
  condition: {
    label: "Condition",
    color: "#d97706",
    light: "#fffbeb",
    border: "#fcd34d",
    emoji: "🔀",
  },
  action: {
    label: "Action",
    color: "#7c3aed",
    light: "#f5f3ff",
    border: "#c4b5fd",
    emoji: "⚙️",
  },
  end: {
    label: "End Flow",
    color: "#dc2626",
    light: "#fef2f2",
    border: "#fca5a5",
    emoji: "🏁",
  },
};

// ─── Custom Node ──────────────────────────────────────────────────────────────
function CustomNode({ data, selected, id }) {
  const cfg = NODE_CONFIG[data.nodeType] || NODE_CONFIG.message;

  return (
    <div
      style={{
        background: "white",
        border: `2px solid ${selected ? cfg.color : cfg.border}`,
        borderRadius: 14,
        minWidth: 200,
        maxWidth: 240,
        boxShadow: selected
          ? `0 0 0 3px ${cfg.color}33, 0 8px 24px rgba(0,0,0,0.12)`
          : "0 2px 10px rgba(0,0,0,0.08)",
        transition: "box-shadow 0.2s, border-color 0.2s",
        overflow: "hidden",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: cfg.color,
          padding: "9px 14px",
          display: "flex",
          alignItems: "center",
          gap: 7,
        }}
      >
        <span style={{ fontSize: 14 }}>{cfg.emoji}</span>
        <span
          style={{
            color: "white",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {cfg.label}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: "10px 14px", background: cfg.light }}>
        <p
          style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}
        >
          {data.label}
        </p>

        {data.message && (
          <p
            style={{
              margin: "5px 0 0",
              fontSize: 11,
              color: "#475569",
              lineHeight: 1.5,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {data.message}
          </p>
        )}
        {data.keyword && (
          <p
            style={{
              margin: "5px 0 0",
              fontSize: 10.5,
              color: cfg.color,
              fontWeight: 600,
            }}
          >
            🔑 {data.keyword}
          </p>
        )}
        {data.condition && (
          <p
            style={{
              margin: "5px 0 0",
              fontSize: 10.5,
              color: cfg.color,
              fontWeight: 600,
            }}
          >
            ⚡ {data.condition}: <em>{data.value}</em>
          </p>
        )}
        {data.action && (
          <p
            style={{
              margin: "5px 0 0",
              fontSize: 10.5,
              color: cfg.color,
              fontWeight: 600,
            }}
          >
            🔧 {data.action.replace(/_/g, " ")}
          </p>
        )}
      </div>

      {/* Edit button */}
      {selected && (
        <div
          style={{
            borderTop: `1px solid ${cfg.border}`,
            background: "white",
            padding: "6px 10px",
            display: "flex",
            gap: 5,
          }}
        >
          <button
            onClick={() => data.onEdit(id)}
            style={{
              flex: 1,
              padding: "5px 0",
              border: "none",
              background: cfg.light,
              color: cfg.color,
              borderRadius: 7,
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <FiEdit2 size={10} /> Edit
          </button>
          {data.nodeType !== "trigger" && (
            <button
              onClick={() => data.onDelete(id)}
              style={{
                padding: "5px 10px",
                border: "none",
                background: "#fef2f2",
                color: "#dc2626",
                borderRadius: 7,
                cursor: "pointer",
                fontSize: 11,
              }}
            >
              <FiTrash2 size={10} />
            </button>
          )}
        </div>
      )}

      {/* Handles */}
      {data.nodeType !== "trigger" && (
        <Handle
          type="target"
          position={Position.Left}
          style={{
            width: 12,
            height: 12,
            background: "#94a3b8",
            border: "2px solid white",
          }}
        />
      )}
      {data.nodeType !== "end" && (
        <Handle
          type="source"
          position={Position.Right}
          style={{
            width: 12,
            height: 12,
            background: cfg.color,
            border: "2px solid white",
          }}
        />
      )}
    </div>
  );
}

const nodeTypes = { custom: CustomNode };

// ─── Edit Node Modal ──────────────────────────────────────────────────────────
function EditModal({ node, onSave, onClose }) {
  const [form, setForm] = useState({ ...node.data });
  const cfg = NODE_CONFIG[node.data.nodeType] || NODE_CONFIG.message;

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: 18,
          width: 460,
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 30px 70px rgba(0,0,0,0.25)",
          fontFamily: "'Segoe UI', system-ui, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            background: cfg.color,
            padding: "18px 22px",
            borderRadius: "18px 18px 0 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ color: "white", fontWeight: 800, fontSize: 15 }}>
            {cfg.emoji} Edit — {cfg.label}
          </span>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: 8,
              padding: "5px 9px",
              cursor: "pointer",
              color: "white",
              fontSize: 16,
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* Label */}
          <Field label="Node Label">
            <input
              value={form.label || ""}
              onChange={(e) => set("label", e.target.value)}
              style={inputStyle}
              placeholder="Enter node name..."
            />
          </Field>

          {/* Trigger fields */}
          {form.nodeType === "trigger" && (
            <>
              <Field label="Trigger Type">
                <select
                  value={form.triggerType || "keyword"}
                  onChange={(e) => set("triggerType", e.target.value)}
                  style={inputStyle}
                >
                  <option value="keyword">Keyword Match</option>
                  <option value="any">Any Message</option>
                  <option value="new_conversation">New Conversation</option>
                  <option value="button_click">Button Click</option>
                </select>
              </Field>
              {form.triggerType === "keyword" && (
                <Field label="Keywords (comma separated)">
                  <input
                    value={form.keyword || ""}
                    onChange={(e) => set("keyword", e.target.value)}
                    style={inputStyle}
                    placeholder="hello, hi, help, start"
                  />
                </Field>
              )}
            </>
          )}

          {/* Message fields */}
          {form.nodeType === "message" && (
            <>
              <Field label="Message Body">
                <textarea
                  value={form.message || ""}
                  onChange={(e) => set("message", e.target.value)}
                  rows={5}
                  style={{
                    ...inputStyle,
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                  placeholder="Enter WhatsApp message... Use *bold*, _italic_, emojis 🎉"
                />
                <p
                  style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}
                >
                  {(form.message || "").length}/1024 characters
                </p>
              </Field>
              <Field label="Quick Replies (optional)">
                <input
                  value={form.quickReplies || ""}
                  onChange={(e) => set("quickReplies", e.target.value)}
                  style={inputStyle}
                  placeholder="Yes, No, Learn More (comma separated)"
                />
              </Field>
            </>
          )}

          {/* Condition fields */}
          {form.nodeType === "condition" && (
            <>
              <Field label="Condition Type">
                <select
                  value={form.condition || ""}
                  onChange={(e) => set("condition", e.target.value)}
                  style={inputStyle}
                >
                  <option>User input contains</option>
                  <option>User input equals</option>
                  <option>User input starts with</option>
                  <option>Variable equals</option>
                  <option>Tag exists</option>
                </select>
              </Field>
              <Field label="Value(s)">
                <input
                  value={form.value || ""}
                  onChange={(e) => set("value", e.target.value)}
                  style={inputStyle}
                  placeholder="1,2,3 or yes,no"
                />
              </Field>
            </>
          )}

          {/* Action fields */}
          {form.nodeType === "action" && (
            <>
              <Field label="Action Type">
                <select
                  value={form.action || ""}
                  onChange={(e) => set("action", e.target.value)}
                  style={inputStyle}
                >
                  <option value="assign_agent">Assign to Agent</option>
                  <option value="set_variable">Set Variable</option>
                  <option value="send_webhook">Send Webhook</option>
                  <option value="add_tag">Add Tag to Contact</option>
                  <option value="unsubscribe">Unsubscribe Contact</option>
                  <option value="send_template">Send Template Message</option>
                </select>
              </Field>
              {form.action === "send_webhook" && (
                <Field label="Webhook URL">
                  <input
                    value={form.webhookUrl || ""}
                    onChange={(e) => set("webhookUrl", e.target.value)}
                    style={inputStyle}
                    placeholder="https://your-api.com/webhook"
                  />
                </Field>
              )}
              {form.action === "add_tag" && (
                <Field label="Tag Name">
                  <input
                    value={form.tagName || ""}
                    onChange={(e) => set("tagName", e.target.value)}
                    style={inputStyle}
                    placeholder="VIP, lead, interested"
                  />
                </Field>
              )}
            </>
          )}

          {/* Buttons */}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: 11,
                border: "1.5px solid #e2e8f0",
                background: "white",
                borderRadius: 10,
                cursor: "pointer",
                fontSize: 13,
                color: "#64748b",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(node.id, form)}
              style={{
                flex: 1,
                padding: 11,
                border: "none",
                background: cfg.color,
                color: "white",
                borderRadius: 10,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <FiCheck size={13} /> Save Node
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  border: "1.5px solid #e2e8f0",
  borderRadius: 9,
  fontSize: 13,
  outline: "none",
  background: "white",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

function Field({ label, children }) {
  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 700,
          color: "#374151",
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

// ─── Default nodes for new bot ────────────────────────────────────────────────
const makeDefaultFlow = (onEdit, onDelete) => ({
  nodes: [
    {
      id: "n1",
      type: "custom",
      position: { x: 60, y: 180 },
      data: {
        nodeType: "trigger",
        label: "Customer Message",
        triggerType: "keyword",
        keyword: "hello, hi, help",
        onEdit,
        onDelete,
      },
    },
    {
      id: "n2",
      type: "custom",
      position: { x: 340, y: 100 },
      data: {
        nodeType: "message",
        label: "Welcome Message",
        message:
          "👋 Welcome! How can I help?\n\n1️⃣ Order Status\n2️⃣ Product Info\n3️⃣ Talk to Agent",
        onEdit,
        onDelete,
      },
    },
    {
      id: "n3",
      type: "custom",
      position: { x: 620, y: 100 },
      data: {
        nodeType: "condition",
        label: "Check Reply",
        condition: "User input equals",
        value: "1,2,3",
        onEdit,
        onDelete,
      },
    },
    {
      id: "n4",
      type: "custom",
      position: { x: 900, y: 20 },
      data: {
        nodeType: "message",
        label: "Order Status",
        message: "📦 Please share your Order ID.",
        onEdit,
        onDelete,
      },
    },
    {
      id: "n5",
      type: "custom",
      position: { x: 900, y: 180 },
      data: {
        nodeType: "message",
        label: "Product Info",
        message: "🛍️ Visit chatboxx.in/products",
        onEdit,
        onDelete,
      },
    },
    {
      id: "n6",
      type: "custom",
      position: { x: 900, y: 340 },
      data: {
        nodeType: "action",
        label: "Transfer to Agent",
        action: "assign_agent",
        onEdit,
        onDelete,
      },
    },
    {
      id: "n7",
      type: "custom",
      position: { x: 1180, y: 180 },
      data: { nodeType: "end", label: "End Flow", onEdit, onDelete },
    },
  ],
  edges: [
    {
      id: "e1",
      source: "n1",
      target: "n2",
      markerEnd: { type: MarkerType.ArrowClosed },
    },
    {
      id: "e2",
      source: "n2",
      target: "n3",
      markerEnd: { type: MarkerType.ArrowClosed },
    },
    {
      id: "e3",
      source: "n3",
      target: "n4",
      label: "= 1",
      markerEnd: { type: MarkerType.ArrowClosed },
    },
    {
      id: "e4",
      source: "n3",
      target: "n5",
      label: "= 2",
      markerEnd: { type: MarkerType.ArrowClosed },
    },
    {
      id: "e5",
      source: "n3",
      target: "n6",
      label: "= 3",
      markerEnd: { type: MarkerType.ArrowClosed },
    },
    {
      id: "e6",
      source: "n4",
      target: "n7",
      markerEnd: { type: MarkerType.ArrowClosed },
    },
    {
      id: "e7",
      source: "n5",
      target: "n7",
      markerEnd: { type: MarkerType.ArrowClosed },
    },
    {
      id: "e8",
      source: "n6",
      target: "n7",
      markerEnd: { type: MarkerType.ArrowClosed },
    },
  ],
});

// ─── Flow Builder ─────────────────────────────────────────────────────────────
function FlowBuilder({ bot, onBack, onSaved }) {
  const [editingNode, setEditingNode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const handleEdit = useCallback((id) => {
    setNodes((nds) => {
      const found = nds.find((n) => n.id === id);
      if (found) setEditingNode(found);
      return nds;
    });
  }, []);

  const handleDelete = useCallback((id) => {
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  }, []);

  // Inject callbacks into existing node data
  const attachCallbacks = useCallback(
    (nds) =>
      nds.map((n) => ({
        ...n,
        data: { ...n.data, onEdit: handleEdit, onDelete: handleDelete },
      })),
    [handleEdit, handleDelete],
  );

  const defaultFlow = makeDefaultFlow(handleEdit, handleDelete);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    bot.flowData?.nodes
      ? attachCallbacks(bot.flowData.nodes)
      : defaultFlow.nodes,
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    bot.flowData?.edges || defaultFlow.edges,
  );

  const onConnect = useCallback((params) => {
    setEdges((eds) =>
      addEdge(
        {
          ...params,
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { stroke: "#94a3b8", strokeWidth: 2 },
        },
        eds,
      ),
    );
  }, []);

  const addNode = (type) => {
    const id = `n-${Date.now()}`;
    const cfg = NODE_CONFIG[type];
    setNodes((prev) => [
      ...prev,
      {
        id,
        type: "custom",
        position: {
          x: 200 + Math.random() * 300,
          y: 100 + Math.random() * 200,
        },
        data: {
          nodeType: type,
          label: cfg.label,
          onEdit: handleEdit,
          onDelete: handleDelete,
          ...(type === "message" ? { message: "" } : {}),
          ...(type === "trigger"
            ? { triggerType: "keyword", keyword: "" }
            : {}),
          ...(type === "condition"
            ? { condition: "User input contains", value: "" }
            : {}),
          ...(type === "action" ? { action: "assign_agent" } : {}),
        },
      },
    ]);
  };

  const saveNodeEdit = (id, data) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: { ...data, onEdit: handleEdit, onDelete: handleDelete },
            }
          : n,
      ),
    );
    setEditingNode(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanNodes = nodes.map(
        ({ data: { onEdit, onDelete, ...rest }, ...n }) => ({
          ...n,
          data: rest,
        }),
      );
      await axios.put(`/api/chatbots/${bot._id}/flow`, {
        nodes: cleanNodes,
        edges,
      });
      setSavedOk(true);
      onSaved && onSaved();
      setTimeout(() => setSavedOk(false), 2500);
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save flow");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          background: "white",
          borderBottom: "1px solid #e2e8f0",
          padding: "10px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "7px 13px",
              border: "1.5px solid #e2e8f0",
              background: "white",
              borderRadius: 9,
              cursor: "pointer",
              fontSize: 12,
              color: "#64748b",
              fontWeight: 600,
            }}
          >
            <FiArrowLeft size={13} /> Back
          </button>
          <div style={{ width: 1, height: 22, background: "#e2e8f0" }} />
          <div>
            <span style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
              🤖 {bot.name}
            </span>
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                padding: "2px 9px",
                borderRadius: 12,
                fontWeight: 700,
                background: bot.status === "Active" ? "#dcfce7" : "#f1f5f9",
                color: bot.status === "Active" ? "#16a34a" : "#64748b",
              }}
            >
              {bot.status}
            </span>
          </div>
        </div>

        {/* Add Node Buttons */}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {Object.entries(NODE_CONFIG)
            .filter(([k]) => k !== "trigger")
            .map(([key, val]) => (
              <button
                key={key}
                onClick={() => addNode(key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "6px 11px",
                  border: `1.5px solid ${val.border}`,
                  background: val.light,
                  color: val.color,
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                <FiPlus size={11} /> {val.emoji} {val.label}
              </button>
            ))}
          <div
            style={{
              width: 1,
              height: 22,
              background: "#e2e8f0",
              margin: "0 4px",
            }}
          />
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 16px",
              border: "none",
              background: savedOk ? "#16a34a" : "#0f172a",
              color: "white",
              borderRadius: 9,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 700,
              transition: "background 0.3s",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {savedOk ? (
              <>
                <FiCheck size={13} /> Saved!
              </>
            ) : saving ? (
              <>
                <FiRefreshCw size={13} className="animate-spin" /> Saving...
              </>
            ) : (
              <>
                <FiSave size={13} /> Save Flow
              </>
            )}
          </button>
        </div>
      </div>

      {/* React Flow Canvas */}
      <div style={{ flex: 1 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          defaultEdgeOptions={{
            style: { stroke: "#94a3b8", strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed },
          }}
        >
          <Background color="#e2e8f0" gap={20} size={1} />
          <Controls
            style={{
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          />
          <MiniMap
            style={{
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            nodeColor={(n) => NODE_CONFIG[n.data?.nodeType]?.color || "#94a3b8"}
          />
          <Panel position="bottom-center">
            <div
              style={{
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "7px 16px",
                fontSize: 11,
                color: "#94a3b8",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              Drag to move nodes · Connect by dragging from node handles ·
              Scroll to zoom
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {editingNode && (
        <EditModal
          node={editingNode}
          onSave={saveNodeEdit}
          onClose={() => setEditingNode(null)}
        />
      )}
    </div>
  );
}

// ─── Bot Card ─────────────────────────────────────────────────────────────────
function BotCard({ bot, index, onOpen, onDelete, onToggleStatus }) {
  const [menu, setMenu] = useState(false);

  return (
    <div
      style={{
        background: "white",
        border: "1.5px solid #e2e8f0",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        transition: "transform 0.2s, box-shadow 0.2s",
        cursor: "default",
        animation: `fadeUp 0.4s ease both`,
        animationDelay: `${index * 70}ms`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 10px 28px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
      }}
    >
      <div
        style={{
          height: 5,
          background:
            bot.status === "Active"
              ? "linear-gradient(90deg, #16a34a, #22c55e)"
              : "#e2e8f0",
        }}
      />

      <div style={{ padding: "16px 18px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                marginBottom: 8,
              }}
            >
              <code
                style={{
                  fontSize: 9.5,
                  color: "#94a3b8",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  padding: "2px 7px",
                  borderRadius: 5,
                }}
              >
                {bot._id?.substring(0, 16)}...
              </code>
              <span
                style={{
                  fontSize: 10,
                  padding: "2px 8px",
                  borderRadius: 20,
                  fontWeight: 700,
                  background: bot.status === "Active" ? "#dcfce7" : "#f1f5f9",
                  color: bot.status === "Active" ? "#16a34a" : "#64748b",
                }}
              >
                {bot.status}
              </span>
            </div>

            <h3
              style={{
                margin: 0,
                fontSize: 17,
                fontWeight: 800,
                color: "#0f172a",
                letterSpacing: "-0.4px",
              }}
            >
              🤖 {bot.name}
            </h3>

            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexDirection: "column",
                gap: 5,
              }}
            >
              {[
                ["Start Point", bot.startPoint || "—"],
                ["Trigger type", bot.triggerType || "text"],
                ["Created On", new Date(bot.createdAt).toLocaleString()],
                ["Updated On", new Date(bot.updatedAt).toLocaleString()],
              ].map(([label, val]) => (
                <div
                  key={label}
                  style={{ display: "flex", gap: 6, fontSize: 12 }}
                >
                  <span
                    style={{ color: "#64748b", fontWeight: 600, minWidth: 95 }}
                  >
                    {label} :
                  </span>
                  <span style={{ color: "#374151" }}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 3-dot menu */}
          <div style={{ position: "relative" }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMenu((p) => !p);
              }}
              style={{
                padding: "5px 7px",
                border: "1px solid #e2e8f0",
                background: "white",
                borderRadius: 7,
                cursor: "pointer",
                color: "#64748b",
              }}
            >
              <FiMoreVertical size={13} />
            </button>
            {menu && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 5px)",
                  background: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  padding: 5,
                  zIndex: 50,
                  minWidth: 150,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {[
                  {
                    icon:
                      bot.status === "Active" ? FiToggleLeft : FiToggleRight,
                    label: bot.status === "Active" ? "Deactivate" : "Activate",
                    action: () => {
                      onToggleStatus(bot);
                      setMenu(false);
                    },
                    color: bot.status === "Active" ? "#d97706" : "#16a34a",
                  },
                  {
                    icon: FiCopy,
                    label: "Duplicate",
                    action: () => setMenu(false),
                    color: "#374151",
                  },
                  {
                    icon: FiTrash2,
                    label: "Delete",
                    action: () => {
                      onDelete(bot._id);
                      setMenu(false);
                    },
                    color: "#dc2626",
                  },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "100%",
                      padding: "8px 11px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      borderRadius: 7,
                      fontSize: 12.5,
                      color: item.color,
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f8fafc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "none")
                    }
                  >
                    <item.icon size={12} /> {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div
          style={{
            marginTop: 14,
            display: "flex",
            gap: 7,
            borderTop: "1px solid #f1f5f9",
            paddingTop: 13,
          }}
        >
          <button
            onClick={() => onOpen(bot)}
            style={{
              flex: 1,
              padding: "9px 0",
              border: "none",
              background: "#16a34a",
              color: "white",
              borderRadius: 9,
              cursor: "pointer",
              fontSize: 12.5,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
            }}
          >
            <FiPlay size={11} /> Open Flow Builder
          </button>
          <button
            style={{
              padding: "9px 13px",
              border: "1.5px solid #e2e8f0",
              background: "white",
              color: "#64748b",
              borderRadius: 9,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            <FiSettings size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Bot Modal ─────────────────────────────────────────────────────────
function CreateBotModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    name: "",
    triggerType: "text",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      await onSave(form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: 18,
          width: 420,
          boxShadow: "0 30px 70px rgba(0,0,0,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #16a34a, #15803d)",
            padding: "20px 22px",
            borderRadius: "18px 18px 0 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <p
              style={{
                color: "white",
                fontWeight: 800,
                fontSize: 16,
                margin: 0,
              }}
            >
              🤖 Create New Bot
            </p>
            <p style={{ color: "#bbf7d0", fontSize: 12, margin: "3px 0 0" }}>
              Configure your WhatsApp chatbot
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: 8,
              padding: "5px 9px",
              cursor: "pointer",
              color: "white",
              fontSize: 17,
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            padding: 22,
            display: "flex",
            flexDirection: "column",
            gap: 15,
          }}
        >
          <Field label="Bot Name *">
            <input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              style={inputStyle}
              placeholder="e.g. customer_support_bot"
            />
            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>
              Lowercase, no spaces
            </p>
          </Field>
          <Field label="Trigger Type">
            <select
              value={form.triggerType}
              onChange={(e) =>
                setForm((p) => ({ ...p, triggerType: e.target.value }))
              }
              style={inputStyle}
            >
              <option value="text">Text Message</option>
              <option value="keyword">Keyword</option>
              <option value="any">Any Message</option>
              <option value="button_click">Button Click</option>
            </select>
          </Field>
          <Field label="Description (optional)">
            <input
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              style={inputStyle}
              placeholder="What does this bot do?"
            />
          </Field>
          <div style={{ display: "flex", gap: 8, marginTop: 5 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: 11,
                border: "1.5px solid #e2e8f0",
                background: "white",
                borderRadius: 10,
                cursor: "pointer",
                fontSize: 13,
                color: "#64748b",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!form.name.trim() || loading}
              style={{
                flex: 1,
                padding: 11,
                border: "none",
                background: form.name.trim() ? "#16a34a" : "#d1d5db",
                color: "white",
                borderRadius: 10,
                cursor: form.name.trim() ? "pointer" : "not-allowed",
                fontSize: 13,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {loading ? (
                <>
                  <FiRefreshCw size={13} /> Creating...
                </>
              ) : (
                <>
                  <FiPlus size={13} /> Create Bot
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Chatbox Page ────────────────────────────────────────────────────────
export default function Chatbox() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBot, setActiveBot] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");

  // Fetch bots
  useEffect(() => {
    fetchBots();
  }, []);

  const fetchBots = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/chatbots");
      if (res.data.success) setBots(res.data.data);
    } catch (err) {
      console.error("Failed to fetch bots:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (form) => {
    try {
      const res = await axios.post("/api/chatbots", {
        name: form.name.toLowerCase().replace(/\s+/g, "_"),
        triggerType: form.triggerType,
        description: form.description,
      });
      if (res.data.success) {
        setBots((p) => [res.data.data, ...p]);
        setShowCreate(false);
        setActiveBot(res.data.data);
      }
    } catch (err) {
      alert(
        "Failed to create bot: " + (err.response?.data?.error || err.message),
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this bot permanently?")) return;
    try {
      await axios.delete(`/api/chatbots/${id}`);
      setBots((p) => p.filter((b) => b._id !== id));
    } catch (err) {
      alert("Delete failed");
    }
  };

  const handleToggleStatus = async (bot) => {
    const newStatus = bot.status === "Active" ? "Inactive" : "Active";
    try {
      await axios.patch(`/api/chatbots/${bot._id}/status`, {
        status: newStatus,
      });
      setBots((p) =>
        p.map((b) => (b._id === bot._id ? { ...b, status: newStatus } : b)),
      );
    } catch (err) {
      alert("Status update failed");
    }
  };

  const filtered = bots.filter((b) => {
    const ms = b.name.toLowerCase().includes(search.toLowerCase());
    const mf = filter === "All" || b.status === filter;
    return ms && mf;
  });

  // ── Flow Builder View ──
  if (activeBot) {
    return (
      <>
        <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }`}</style>
        <FlowBuilder
          bot={activeBot}
          onBack={() => {
            setActiveBot(null);
            fetchBots();
          }}
          onSaved={fetchBots}
        />
      </>
    );
  }

  // ── Bot List View ──
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }`}</style>

      {/* Banner */}
      {/* <div
        style={{
          background: "linear-gradient(135deg, #16a34a, #15803d)",
          padding: "11px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: 13, color: "white" }}>
          ⚡ Build WhatsApp chatbot flows that respond 24/7 automatically
        </span>
        <button
          style={{
            fontSize: 12,
            color: "#86efac",
            background: "none",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Explore →
        </button>
      </div> */}

      <div style={{ padding: "26px 28px" }}>
        {/* Page Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 800,
                color: "#0f172a",
                letterSpacing: "-0.6px",
              }}
            >
              Chatbot
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
              {bots.length} bots ·{" "}
              {bots.filter((b) => b.status === "Active").length} active
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 20px",
              border: "none",
              background: "#16a34a",
              color: "white",
              borderRadius: 11,
              cursor: "pointer",
              fontSize: 13.5,
              fontWeight: 700,
              boxShadow: "0 4px 16px rgba(22,163,74,0.35)",
            }}
          >
            <FiPlus size={15} /> Create New Bot
          </button>
        </div>

        {/* Stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 14,
            marginBottom: 24,
          }}
        >
          {[
            {
              label: "Total Bots",
              value: bots.length,
              color: "#2563eb",
              bg: "#eff6ff",
              emoji: "🤖",
            },
            {
              label: "Active",
              value: bots.filter((b) => b.status === "Active").length,
              color: "#16a34a",
              bg: "#f0fdf4",
              emoji: "✅",
            },
            {
              label: "Inactive",
              value: bots.filter((b) => b.status === "Inactive").length,
              color: "#d97706",
              bg: "#fffbeb",
              emoji: "⏸️",
            },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                background: s.bg,
                border: `1.5px solid ${s.color}22`,
                borderRadius: 13,
                padding: "14px 18px",
                display: "flex",
                alignItems: "center",
                gap: 13,
              }}
            >
              <span style={{ fontSize: 26 }}>{s.emoji}</span>
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 26,
                    fontWeight: 900,
                    color: s.color,
                    letterSpacing: "-1.5px",
                  }}
                >
                  {s.value}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 11.5,
                    color: "#64748b",
                    fontWeight: 600,
                  }}
                >
                  {s.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Search + Filter */}
        <div style={{ display: "flex", gap: 10, marginBottom: 22 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <FiSearch
              size={13}
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
              }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bots by name..."
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>
          {["All", "Active", "Inactive"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: "9px 18px",
                border: `1.5px solid ${filter === f ? "#16a34a" : "#e2e8f0"}`,
                background: filter === f ? "#f0fdf4" : "white",
                color: filter === f ? "#16a34a" : "#64748b",
                borderRadius: 9,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {f}
            </button>
          ))}
          <button
            onClick={fetchBots}
            style={{
              padding: "9px 12px",
              border: "1.5px solid #e2e8f0",
              background: "white",
              color: "#64748b",
              borderRadius: 9,
              cursor: "pointer",
            }}
          >
            <FiRefreshCw size={14} />
          </button>
        </div>

        {/* Bot Grid */}
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "70px 0", color: "#94a3b8" }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                border: "3px solid #e2e8f0",
                borderTop: "3px solid #16a34a",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto 12px",
              }}
            />
            <p style={{ fontSize: 14 }}>Loading bots...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        ) : filtered.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "70px 20px",
              color: "#94a3b8",
            }}
          >
            <span style={{ fontSize: 48, display: "block", marginBottom: 12 }}>
              🤖
            </span>
            <p
              style={{
                fontSize: 16,
                fontWeight: 700,
                margin: "0 0 5px",
                color: "#374151",
              }}
            >
              {search ? "No bots found" : "No bots yet"}
            </p>
            <p style={{ fontSize: 13, margin: "0 0 18px" }}>
              {search
                ? "Try a different search"
                : "Create your first chatbot to auto-respond on WhatsApp"}
            </p>
            {!search && (
              <button
                onClick={() => setShowCreate(true)}
                style={{
                  padding: "10px 22px",
                  border: "none",
                  background: "#16a34a",
                  color: "white",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <FiPlus size={13} style={{ marginRight: 5 }} />
                Create First Bot
              </button>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 16,
            }}
          >
            {filtered.map((bot, i) => (
              <BotCard
                key={bot._id}
                bot={bot}
                index={i}
                onOpen={setActiveBot}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
              />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateBotModal
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}
